import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { AutoComplete } from '@xing-com/auto-complete';
import { IconLocationPin } from '@xing-com/icons';
import { mediaTopBarVisible } from '@xing-com/layout-tokens';
import { xdlPaletteWhite, spaceM, spaceS, scale130 } from '@xing-com/tokens';

import type { LocationSuggestion } from '../hooks';

import { suggestionRenderer } from './suggestion-renderer';
import { useLocationAutocompletion } from './use-location-autocomplete';

const locationSuggestionToString = (item: LocationSuggestion | null): string =>
  item ? item.name : '';

type Props = {
  value: string;
  onChangeLocation: (value: string) => void;
  onSelectLocation: (item: LocationSuggestion) => void;
  isLocationDisabled?: boolean;
};

export const LocationInput: React.FC<Props> = ({
  value,
  onChangeLocation,
  onSelectLocation,
  isLocationDisabled,
}) => {
  const { formatMessage } = useIntl();
  const { locationSuggestions, fetchLocationSuggestions } =
    useLocationAutocompletion();

  const handleOnChangeLocation = (value: string): void => {
    onChangeLocation(value);

    if (value) {
      fetchLocationSuggestions(value.trim());
    }
  };

  return (
    <Input
      // @ts-expect-error: type type not working
      type="search"
      variant="inputBar"
      textFieldVariant="plain"
      size="large"
      value={value}
      placeholder={formatMessage({
        id: 'JOBS_FIND_JOBS_SEARCH_LOCATION_PLACEHOLDER',
      })}
      label={formatMessage({
        id: 'JOBS_FIND_JOBS_SEARCH_LOCATION_PLACEHOLDER',
      })}
      suggestions={locationSuggestions}
      onChange={handleOnChangeLocation}
      onSelect={onSelectLocation}
      itemRenderer={suggestionRenderer(value)}
      suggestionToString={locationSuggestionToString}
      id="location-input"
      icon={IconLocationPin}
      disabled={isLocationDisabled}
      clearButtonProps={{
        onMouseDown: (e) => {
          // prevent input focus -> unfocus -> focus
          e.preventDefault();
        },
      }}
    />
  );
};

const Input = styled(AutoComplete<LocationSuggestion>)`
  && {
    width: 100%;

    & input {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      transition: border 0.18s ease-in-out;

      &::-webkit-search-cancel-button {
        display: none;
      }

      @media ${mediaTopBarVisible} {
        /*
        iOS border radius appearance bug: https://stackoverflow.com/a/2918716/1025732
        Appearance is set by normalized styles here: brewery-packages/layout/global-styles/src/normalize-styles.ts
        */
        appearance: none;
      }
    }

    & button[type='reset'] {
      height: ${scale130};
      top: ${spaceS};
      left: ${spaceM};
      right: auto;
      background-color: ${xdlPaletteWhite};
    }
  }
`;
